// Where a client is on their journey, in plain words (Nic 2026-10-10: the
// client couldn't tell what was happening). One source for the tracker, the
// headline and the "Right now" card.
//
// Steps: Signed up > VA.gov > Your documents > Building your claim >
// With the VA > Complete. The claim's status decides the late steps; before a
// Claims Manager, their Client Onboarding record does (VA.gov first, then
// documents, then being matched with a Claims Manager).

export const STEPS = [
  { key: 'signed', label: 'Signed up', short: 'Signed' },
  { key: 'vagov', label: 'VA.gov account', short: 'VA.gov' },
  { key: 'documents', label: 'Your documents', short: 'Documents' },
  { key: 'building', label: 'Building your claim', short: 'Building' },
  { key: 'va', label: 'With the VA', short: 'With VA' },
  { key: 'complete', label: 'Complete', short: 'Complete' },
];

const BUILDING = ['in process', 'awaiting internal review', 'internal review approved'];
const COMPLETE = ['invoiced', 'payment plan', 'dispute hold', 'paid'];

// now: the "Right now" card's kind.
//   vagov | documents | review (sent everything, specialist reviewing) |
//   matching (picking their Claims Manager) | building | va | complete |
//   paid | closed
// Where a checklist step sits on their portal (Nic 2026-10-23):
//   todo       still to send
//   requested  their specialist requested it (the service treatment record);
//              listed last and counted (Nic 2026-10-26), with a soft upload for
//              when it arrives
//   review     they sent a file; their specialist hasn't checked it off yet
//   done       checked off by their specialist
//   na         doesn't apply to them: finished, never "checked off"
export const stepKindOf = (item) =>
  item.status === 'done'
    ? item.notNeeded
      ? 'na'
      : 'done'
    : item.uploads
      ? 'review'
      : item.requested
        ? 'requested'
        : 'todo';

// What the client still has to send (Nic 2026-10-23): not done, and nothing
// sent for it yet. A step with a file waiting on their specialist's review
// stops counting, so "Send us N more" never asks again for what they sent.
// One requested from the VA (the service treatment record) still counts (Nic
// 2026-10-26): it's on its way to them and they upload it when it arrives,
// so a list of two never reads "Send us 1 more".
export const stillNeeded = (items = []) =>
  items.filter((i) => i.key !== 'va_gov' && !i.auto && i.status !== 'done' && !i.uploads);

export const journeyOf = ({ claim, onboarding }) => {
  const status = claim?.claimStatus || null;
  const ob = onboarding && onboarding.state ? onboarding : null;
  let current;
  let now;
  if (status === 'dead' || ob?.state === 'dead') {
    return { current: null, now: 'closed', closed: true, steps: STEPS.map((s) => ({ ...s, state: 'upcoming' })) };
  }
  if (status === 'paid') {
    current = 'complete';
    now = 'paid';
  } else if (COMPLETE.includes(status)) {
    current = 'complete';
    now = 'complete';
  } else if (status === 'submitted to va') {
    current = 'va';
    now = 'va';
  } else if (BUILDING.includes(status) || ob?.state === 'with_cm') {
    current = 'building';
    now = 'building';
  } else if (ob?.state === 'ready_for_cm') {
    current = 'building';
    now = 'matching';
  } else if (ob && !ob.vaGov?.done) {
    current = 'vagov';
    now = 'vagov';
  } else {
    current = 'documents';
    // Everything on them is in: their specialist is reviewing it.
    now = ob && stillNeeded(ob.items).length === 0 ? 'review' : 'documents';
  }
  const at = STEPS.findIndex((s) => s.key === current);
  const allDone = now === 'paid';
  return {
    current,
    now,
    closed: false,
    index: at,
    steps: STEPS.map((s, i) => ({
      ...s,
      state: allDone || i < at ? 'done' : i === at ? 'current' : 'upcoming',
    })),
  };
};

// The one line under the greeting.
export const headlineOf = (journey, { todoCount = 0 } = {}) => {
  switch (journey.now) {
    case 'vagov':
      return 'First things first: your VA.gov account.';
    case 'documents':
      return todoCount
        ? `We need ${todoCount} more thing${todoCount === 1 ? '' : 's'} from you.`
        : 'Your documents are coming together.';
    case 'review':
      return "You've sent everything we asked for. Your specialist is reviewing it.";
    case 'matching':
      return "Onboarding is done. We're matching you with your Claims Manager.";
    case 'building':
      return 'Your Claims Manager is building your claim.';
    case 'va':
      return 'Your claim is with the VA.';
    case 'complete':
      return 'Your claim is complete.';
    case 'paid':
      return "You're all set. Thank you for trusting us.";
    case 'closed':
      return 'Your claim is closed.';
    default:
      return '';
  }
};
